import { Registration } from '@stax-core/types-omni';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { coreapi, onboardingapi } from '../../api';
import { AuthStore, BrandStore, RegistrationStore } from '../../context';
import { usePrevious } from '../../hooks';
import * as businessFieldsUtil from '../../pages/business/BusinessFields.util';
import * as financialFieldsUtil from '../../pages/financial/FinancialFields.util';
import * as paymentsFieldsUtil from '../../pages/payments/PaymentsFields.util';
import {
  ApplicationModalProps,
  HelloSignConfig,
} from './ApplicationModal.types';
import {
  getHellosignTemplate,
  mapRegistrationToHellosignTemplate,
} from './ApplicationModal.util';
import { HelloSignModal } from './HelloSignModal';

export const ApplicationModal: FC<ApplicationModalProps> = props => {
  const { applicationType, isOpen, onLoad, onSign, onClose, formValues } =
    props;

  const {
    state: { auth },
  } = useContext(AuthStore);

  const {
    state: { registration: storedRegistration },
  } = useContext(RegistrationStore);

  const {
    state: { brand },
  } = useContext(BrandStore);

  const brandHellosignTemplateId = brand && brand.registrationHellosignId;

  const [helloSignConfig, setHelloSignConfig] = useState<HelloSignConfig>({});

  const authToken = auth?.token;
  const userEmail = auth?.user?.email;
  const wasOpen = usePrevious(isOpen);

  const openApplication = useCallback(async () => {
    if (!isOpen || !authToken || !userEmail) return;

    const registration = {
      ...storedRegistration,
      ...businessFieldsUtil.mapFormValuesToPayload(formValues),
      ...financialFieldsUtil.mapFormValuesToPayload(formValues),
      ...paymentsFieldsUtil.mapFormValuesToPayload(formValues),
    } as Registration;

    const principalSigner = {
      // role must be 'Owner' according to the way the HelloSign application is set up.
      role: 'Owner',
      name: `${registration.first_name} ${registration.last_name}`,
      email: userEmail,
    };

    const counterSigner = {
      // role must be 'Fattmerchant' according to the way the HelloSign application is set up.
      role: 'Fattmerchant',
      name: 'Fattmerchant',
      email: 'signup@fattmerchant.com',
    };

    switch (applicationType) {
      case 'iso':
      case 'fis_priority_combined':
      case 'tsys_direct':
      case 'fd_direct':
      case 'priority':
      case 'payfac':
      case 'forte':
      case 'elavon':
      case 'worldpay_swipe_simple':
      case 'worldpay_dejavoo':
        let isoFields = mapRegistrationToHellosignTemplate(
          registration,
          applicationType,
        );

        // if this brand has a particular hellosign template
        // add in any other fields that this template needs
        if (brandHellosignTemplateId) {
          const template = await coreapi.get(
            authToken,
            `hellosign/${brandHellosignTemplateId}`,
          );
          const templateCustomFields = template.custom_fields.map(
            field => field.name,
          );

          // map the required template fields
          const additionalMappedFields = await onboardingapi.post(
            authToken,
            `mapping/registration?custom_fields=${templateCustomFields}`,
            registration,
          );

          // TODO: KRK-28 clean this up and only use mapped fields
          // loop through all of the template fields to make a new combinedFields object
          // if the field doesn't exist in the mapping fn in underwriting (additionalMappedFields)
          // then fall back to using the value in the local mapping fn (isoFields object)
          const combinedFields = templateCustomFields.reduce(
            (combinedFields, customFieldName) => {
              combinedFields[customFieldName] =
                additionalMappedFields[customFieldName] ||
                isoFields[customFieldName];
              return combinedFields;
            },
            {},
          );

          isoFields = combinedFields;
        }

        setHelloSignConfig({
          templateId:
            brandHellosignTemplateId || getHellosignTemplate(applicationType),
          fields: isoFields,
          signers: [principalSigner, counterSigner],
        });
        onLoad();
        break;
      default:
        break;
    }
  }, [
    authToken,
    userEmail,
    applicationType,
    isOpen,
    setHelloSignConfig,
    formValues,
    onLoad,
    storedRegistration,
    brandHellosignTemplateId,
  ]);

  useEffect(() => {
    if (isOpen && !wasOpen) openApplication();
  }, [isOpen, wasOpen, openApplication]);

  return (
    <HelloSignModal
      isOpen={!!helloSignConfig.fields}
      templateId={helloSignConfig.templateId!}
      onSign={onSign}
      onClose={() => {
        setHelloSignConfig({});
        onClose();
      }}
      fields={helloSignConfig.fields}
      signers={helloSignConfig.signers}
    />
  );
};
